import {
  User,
  UserManager,
  UserManagerSettings,
  WebStorageStateStore,
} from 'oidc-client-ts';

const getRequiredEnvironmentVariable = (name: string): string => {
  const value = process.env[name];
  if (!value) {
    throw new Error(`Missing OIDC configuration variable: ${name}`);
  }
  return value;
};

const getSettings = (): UserManagerSettings => {
  const apiAudience = process.env.REACT_APP_API_AUDIENCE;
  return {
    authority: getRequiredEnvironmentVariable('REACT_APP_OIDC_ISSUER'),
    client_id: getRequiredEnvironmentVariable('REACT_APP_OIDC_CLIENT_ID'),
    redirect_uri: getRequiredEnvironmentVariable('REACT_APP_OIDC_REDIRECT_URI'),
    silent_redirect_uri: getRequiredEnvironmentVariable('REACT_APP_OIDC_REDIRECT_URI'),
    post_logout_redirect_uri: getRequiredEnvironmentVariable('REACT_APP_OIDC_POST_LOGOUT_REDIRECT_URI'),
    response_type: 'code',
    disablePKCE: false,
    scope: process.env.REACT_APP_OIDC_SCOPE || 'openid profile email',
    ...(apiAudience ? { extraQueryParams: { resource: apiAudience } } : {}),
    loadUserInfo: true,
    automaticSilentRenew: false,
    monitorSession: false,
    stateStore: new WebStorageStateStore({ store: window.sessionStorage }),
    userStore: new WebStorageStateStore({ store: window.sessionStorage }),
  };
};

let manager: UserManager | undefined;
let activeUser: User | null = null;
let refreshPromise: Promise<User | null> | null = null;

const getUserManager = (): UserManager => {
  if (!manager) {
    manager = new UserManager(getSettings());
  }
  return manager;
};

const hasOidcCallbackParameters = (): boolean => {
  const params = new URLSearchParams(window.location.search);
  return params.has('code') || params.has('error');
};

const cleanCallbackUrl = (): void => {
  window.history.replaceState({}, document.title, window.location.pathname + window.location.hash);
};

const signin = async (): Promise<void> => {
  await getUserManager().signinRedirect();
};

const getUser = async (): Promise<User | null> => {
  if (activeUser) {
    return activeUser;
  }
  activeUser = await getUserManager().getUser();
  return activeUser;
};

const init = async (): Promise<User | null> => {
  const userManager = getUserManager();

  if (hasOidcCallbackParameters()) {
    activeUser = await userManager.signinRedirectCallback();
    cleanCallbackUrl();
  } else {
    activeUser = await userManager.getUser();
  }

  if (!activeUser || activeUser.expired) {
    activeUser = null;
    await signin();
    return null;
  }

  return activeUser;
};

const getDisplayName = (user: User): string => {
  const { profile } = user;
  if (typeof profile.name === 'string' && profile.name) {
    return profile.name;
  }
  if (typeof profile.given_name === 'string' && profile.given_name) {
    return [profile.given_name, profile.family_name].filter(Boolean).join(' ');
  }
  return typeof profile.preferred_username === 'string' ? profile.preferred_username : '';
};

const refreshToken = async (): Promise<string | null> => {
  const currentUser = await getUser();
  if (!currentUser) {
    return null;
  }

  const secondsUntilExpiry = currentUser.expires_at
    ? currentUser.expires_at - Math.floor(Date.now() / 1000)
    : 0;
  if (secondsUntilExpiry > 60) {
    return currentUser.access_token;
  }

  if (!refreshPromise) {
    refreshPromise = getUserManager()
      .signinSilent()
      .then((refreshedUser) => {
        activeUser = refreshedUser;
        return refreshedUser;
      })
      .catch(() => null)
      .finally(() => {
        refreshPromise = null;
      });
  }

  const refreshedUser = await refreshPromise;
  return refreshedUser?.access_token || null;
};

const logout = async (): Promise<void> => {
  const currentUser = await getUserManager().getUser();
  activeUser = null;
  await getUserManager().signoutRedirect({
    id_token_hint: currentUser?.id_token,
    post_logout_redirect_uri: getRequiredEnvironmentVariable('REACT_APP_OIDC_POST_LOGOUT_REDIRECT_URI'),
  });
  await getUserManager().removeUser();
};

const clearUser = async (): Promise<void> => {
  activeUser = null;
  await getUserManager().removeUser();
};

export {
  clearUser,
  getDisplayName,
  getUser,
  init,
  logout,
  refreshToken,
  signin,
};
